<template>
  <agent-default>
    <div class="tw-w-full">
      <div class="tw-px-4 md:tw-px-8 tw-pt-6 tw-pb-10">

        <!-- Page Header -->
        <header class="page-header tw-mb-6">
          <div>
            <h2 class="page-title">Conversions</h2>
            <p class="page-subtitle">View all client conversions and policy details.</p>
          </div>
          <div class="stats-chip">
            <span class="stats-chip-label">Total Conversions</span>

            <span v-if="loading" class="shimmer shimmer-stat-value"></span>

            <span v-else class="stats-chip-value">
              {{ totalCount }}
            </span>
          </div>
        </header>

        <!-- Type Switch + Search -->
        <div class="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center sm:tw-justify-between tw-gap-4 tw-mb-5">
          <div class="type-switch">
            <button
              :class="['type-switch-btn', { 'type-switch-btn--active': portalTransactionType === 'N' }]"
              @click="setPortalType('N')"
            >
              Non Portal
            </button>
            <button
              :class="['type-switch-btn', { 'type-switch-btn--active': portalTransactionType === 'Y' }]"
              @click="setPortalType('Y')"
            >
              Portal
            </button>
          </div>
          <div class="search-wrapper tw-w-full sm:tw-max-w-[380px]">
            <i class="ph ph-magnifying-glass search-icon" style="color: #94A3B8; font-size: 20px;"></i>
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search by client name..."
              class="search-input"
            />
            <button v-if="searchQuery" class="search-clear" @click="searchQuery = ''">
              <i class="ph ph-x" style="color: #94A3B8; font-size: 18px;"></i>
            </button>
          </div>
        </div>

        <!-- Loading Skeleton -->
          <div v-if="loading" class="claims-card">
            <div class="table-scroll-wrap">
              <table class="claims-table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Product</th>
                    <th>Premium(Ksh)</th>
                    <th>Commissions</th>
                    <th>Cover From</th>
                    <th>Cover To</th>
                    <th>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  <tr
                    v-for="n in 6"
                    :key="`conversion-skeleton-${n}`"
                    class="claims-row skeleton-row"
                  >
                    <td>
                      <div class="shimmer shimmer-client"></div>
                    </td>

                    <td>
                      <div class="shimmer shimmer-product"></div>
                    </td>

                    <td>
                      <div class="shimmer shimmer-amount"></div>
                    </td>

                    <td>
                      <div class="shimmer shimmer-amount"></div>
                    </td>

                    <td>
                      <div class="shimmer shimmer-date"></div>
                    </td>

                    <td>
                      <div class="shimmer shimmer-date"></div>
                    </td>

                    <td>
                      <div class="shimmer shimmer-action"></div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Table Card -->
          <div v-else class="claims-card">
          <div v-if="filteredConversions.length > 0" class="table-scroll-wrap">
            <table class="claims-table">
            <thead>
              <tr>
                <th>Client</th>
                <th>Product</th>
                <th>Premium(Ksh)</th>
                <th>Commissions</th>
                <th>Cover From</th>
                <th>Cover To</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(conv, idx) in filteredConversions"
                :key="conv.policy || `conv-${idx}`"
                class="claims-row"
                @click="openDetailsModal(conv)"
              >
                <td>
                  <div class="client-name">{{ conv.client }}</div>
                </td>
                <td>
                  <span class="cover-badge badge-comprehensive">{{ conv.product }}</span>
                </td>
                <td class="cell-text">{{ (conv.value || 0).toLocaleString() }}</td>
                <td class="cell-text">{{ (conv.commissionAmount ?? 0).toLocaleString() }}</td>
                <td class="cell-text cell-muted">{{ formatDate(conv.coverFrom) }}</td>
                <td class="cell-text cell-muted">{{ formatDate(conv.coverTo) }}</td>
                <td class="tw-flex tw-items-center tw-gap-2">
                  <button class="btn-view" @click.stop="openDetailsModal(conv)">
                    View Details
                  </button>
                  <v-btn
                    icon small
                    title="Policy Documents"
                    @click.stop="openDocsModal(conv)"
                    style="color:#2B6CB0;"
                  >
                    <v-icon small>mdi-folder-multiple-outline</v-icon>
                  </v-btn>
                </td>
              </tr>
            </tbody>
          </table>
          </div>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <i class="ph ph-file-text" style="font-size: 60px; color: #CBD5E1;"></i>
            <p class="empty-title">No conversions found</p>
            <p class="empty-subtitle">Try adjusting your search query.</p>
          </div>
        </div>

      </div>
    </div>

    <!-- Conversion Details Modal -->
    <v-dialog v-model="detailsModalVisible" max-width="480" persistent content-class="conversion-modal">
      <v-card v-if="selectedConversion" class="conversion-modal-card">
        <div class="conversion-modal-header">
          <h3 class="conversion-modal-title">Conversion Details</h3>
          <v-btn icon small @click="closeDetailsModal" class="conversion-modal-close">
            <i class="ph ph-x"></i>
          </v-btn>
        </div>
        <v-divider></v-divider>
        <v-card-text class="conversion-modal-body">
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Client</span>
            <span class="conversion-detail-value">{{ selectedConversion.client }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Product</span>
            <span class="conversion-detail-value">
              <span class="badge badge-info">{{ selectedConversion.product }}</span>
            </span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Policy No</span>
            <span class="conversion-detail-value">{{ selectedConversion.policy || '—' }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Premium</span>
            <span class="conversion-detail-value">{{ formatCurrency(selectedConversion.value) }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Cover From</span>
            <span class="conversion-detail-value">{{ formatDate(selectedConversion.coverFrom) }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Cover To</span>
            <span class="conversion-detail-value">{{ formatDate(selectedConversion.coverTo) }}</span>
          </div>
          <div class="conversion-detail-row">
            <span class="conversion-detail-label">Renewal Date</span>
            <span class="conversion-detail-value">{{ formatDate(selectedConversion.renewalDate) }}</span>
          </div>
          <div class="conversion-detail-row" v-if="selectedConversion.batchNo">
            <span class="conversion-detail-label">Batch No</span>
            <span class="conversion-detail-value">{{ selectedConversion.batchNo }}</span>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="conversion-modal-actions">
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="closeDetailsModal">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Documents Modal -->
    <v-dialog v-model="docsModalVisible" max-width="680" content-class="docs-modal">
      <v-card class="docs-modal-card">
        <div class="docs-modal-header">
          <div>
            <h3 class="docs-modal-title">Policy Documents</h3>
            <p class="docs-modal-subtitle">{{ docsModalTitle }}</p>
          </div>
          <v-btn icon small @click="docsModalVisible = false">
            <i class="ph ph-x" style="font-size:18px; color:#718096;"></i>
          </v-btn>
        </div>
        <v-divider></v-divider>

        <!-- Loading -->
        <div v-if="docsModalLoading" class="docs-modal-loader">
          <v-progress-circular indeterminate color="#2BABE2" size="36"></v-progress-circular>
          <p class="tw-mt-3 tw-text-sm" style="color:#94A3B8;">Fetching documents…</p>
        </div>

        <!-- Error -->
        <div v-else-if="docsModalError" class="docs-modal-empty">
          <i class="ph ph-warning-circle" style="font-size:40px; color:#FC8181;"></i>
          <p class="tw-mt-2 tw-text-sm" style="color:#718096;">{{ docsModalError }}</p>
        </div>

        <!-- Empty -->
        <div v-else-if="docsModalList.length === 0" class="docs-modal-empty">
          <i class="ph ph-folder-open" style="font-size:40px; color:#CBD5E0;"></i>
          <p class="tw-mt-2 tw-text-sm" style="color:#94A3B8;">No documents found for this policy.</p>
        </div>

        <!-- Document List -->
        <div v-else class="docs-modal-list">
          <div
            v-for="doc in docsModalList"
            :key="doc.id"
            class="doc-list-item"
          >
            <div class="doc-list-icon">
              <i
                :class="doc.mimeType === '.pdf' ? 'ph ph-file-pdf' : 'ph ph-file'"
                style="font-size:22px;"
                :style="{ color: doc.mimeType === '.pdf' ? '#E53E3E' : '#718096' }"
              ></i>
            </div>
            <div class="doc-list-info">
              <p class="doc-list-name">{{ doc.actualName }}</p>
              <div class="doc-list-meta">
                <span class="doc-type-badge">{{ doc.docType }}</span>
                <span class="doc-date">{{ doc.dateCreated }}</span>
              </div>
            </div>
            <v-btn
              small outlined color="primary"
              class="doc-view-btn"
              @click="openDocument(doc)"
            >
              <v-icon x-small left>mdi-eye-outline</v-icon>
              View
            </v-btn>
          </div>
        </div>

        <v-divider></v-divider>
        <v-card-actions style="padding: 12px 20px;">
          <v-spacer></v-spacer>
          <v-btn text color="primary" @click="docsModalVisible = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Document Viewer Modal -->
    <v-dialog
      v-model="docViewerVisible"
      max-width="1100"
      max-height="90vh"
      content-class="doc-viewer-dialog"
      @click:outside="closeDocViewer"
    >
      <v-card class="doc-viewer-card">
        <!-- Header -->
        <div class="doc-viewer-header">
          <div class="tw-flex tw-items-center tw-gap-3">
            <v-icon color="#E53E3E" size="22">mdi-file-pdf-box</v-icon>
            <div>
              <h3 class="doc-viewer-title">{{ docViewerTitle }}</h3>
              <p class="doc-viewer-subtitle">Document Preview</p>
            </div>
          </div>
          <div class="tw-flex tw-items-center tw-gap-2">
            <v-btn
              v-if="docViewerUrl"
              small outlined color="primary"
              @click="openDocFull"
            >
              <v-icon x-small left>mdi-open-in-new</v-icon>
              Open Full
            </v-btn>
            <v-btn icon small @click="closeDocViewer">
              <v-icon size="20" color="#718096">mdi-close</v-icon>
            </v-btn>
          </div>
        </div>
        <v-divider></v-divider>

        <!-- Loading state -->
        <div v-if="docViewerLoading" class="doc-viewer-loading">
          <v-progress-circular indeterminate color="#00AEEF" size="48"></v-progress-circular>
          <p class="tw-mt-4" style="color:#94A3B8; font-size:0.9rem;">Loading document…</p>
        </div>

        <!-- iframe -->
        <div v-else-if="docViewerUrl" class="doc-viewer-frame-wrap">
          <iframe
            :src="docViewerUrl"
            class="doc-viewer-iframe"
            frameborder="0"
          ></iframe>
        </div>
      </v-card>
    </v-dialog>
  </agent-default>
</template>

<script>
import axios from 'axios';
import AgentDefault from '@/components/layout/AgentDefault';
import { mapGetters } from 'vuex';
import { trackAgentInteraction } from '@/utils/agentAnalytics';

export default {
  name: 'AgentConversions',
  components: { AgentDefault },
  data() {
    return {
      loading: true,
      searchQuery: '',
      portalTransactionType: 'N',
      conversions: [],
      detailsModalVisible: false,
      selectedConversion: null,
      docsModalVisible: false,
      docsModalLoading: false,
      docsModalTitle: '',
      docsModalList: [],
      docsModalError: null,
      docViewerVisible: false,
      docViewerUrl: '',
      docViewerTitle: '',
      docViewerLoading: false,
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticatedUser', 'isAuthenticated']),
    totalCount() {
      return this.conversions.length;
    },
    filteredConversions() {
      if (!this.searchQuery || !this.searchQuery.trim()) return this.conversions;
      const query = this.searchQuery.trim().toLowerCase();
      return this.conversions.filter((c) => (c.client || '').toLowerCase().includes(query));
    },
  },
  watch: {
    portalTransactionType() {
      this.fetchConversions();
    },
  },
  mounted() {
    this.fetchConversions();
  },
  methods: {
    setPortalType(type) {
      if (type === 'N' || type === 'Y') {
        this.portalTransactionType = type;
        trackAgentInteraction(this, 'conversions_toggle_type', {
          conversion_type: type === 'Y' ? 'portal' : 'non_portal',
        });
      }
    },
    async fetchConversions() {
      const agnCode = this.authenticatedUser?.['custom:agent_code'];
      const email = this.authenticatedUser?.email;
      if (!agnCode && !email) {
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const baseUrl = '/customer/gis/agent/conversions';
        const params = `limit=1000&portalTransaction=${this.portalTransactionType}`;
        const url = agnCode
          ? `${baseUrl}?agn_code=${agnCode}&${params}`
          : `${baseUrl}?email=${email}&${params}`;
        const res = await axios.get(url, { validateStatus: () => true });
        if (res.status !== 200 || !res.data) {
          this.conversions = [];
          return;
        }
        const raw = res.data?.object?.conversions ?? res.data?.conversions ?? res.data?.object ?? res.data?.data;
        const list = Array.isArray(raw) ? raw : [];
        const sorted = [...list].sort((a, b) => (b.coverFrom || '').localeCompare(a.coverFrom || ''));
        this.conversions = sorted.map((c) => ({
          client: c.clientName ?? c.client ?? '',
          product: c.product ?? '',
          value: c.premium ?? c.value ?? 0,
          commissionAmount: c.commissionAmount ?? 0,
          policy: c.policyNo ?? c.policy ?? null,
          coverFrom: c.coverFrom ?? null,
          coverTo: c.coverTo ?? null,
          renewalDate: c.renewalDate ?? null,
          batchNo: c.batchNo ?? null,
          currency: c.currency ?? 'KSHS',
          risks: Array.isArray(c.risks) ? c.risks : [],
        }));
      } catch {
        this.conversions = [];
      } finally {
        this.loading = false;
      }
    },
    openDetailsModal(conv) {
      trackAgentInteraction(this, 'conversions_open_details', {
        policy_number: conv?.policy || 'unknown',
      });
      const policyNo = conv.policy || '';
      if (policyNo) {
        const slug = String(policyNo).replace(/\//g, '-');
        try {
          sessionStorage.setItem(`agentConversion_${slug}`, JSON.stringify(conv));
        } catch {
          /* ignore */
        }
        this.$router.push({ name: 'AgentConversionDetails', params: { policyNo: slug } });
      } else {
        this.selectedConversion = conv;
        this.detailsModalVisible = true;
      }
    },
    closeDetailsModal() {
      this.detailsModalVisible = false;
      this.selectedConversion = null;
    },
    async openDocsModal(conv) {
      trackAgentInteraction(this, 'conversions_open_documents', {
        policy_number: conv?.policy || conv?.batchNo || 'unknown',
      });
      const policyNo = conv.policy || conv.batchNo;
      if (!policyNo) return;

      this.docsModalTitle = `Documents — ${policyNo}`;
      this.docsModalList = [];
      this.docsModalError = null;
      this.docsModalVisible = true;
      this.docsModalLoading = true;

      try {
        const res = await axios.get(
          `/customer/document-api/v1/getNBDocByPolicyNo?policyNo=${policyNo}`,
          { validateStatus: () => true },
        );
        if (res.status === 200 && Array.isArray(res.data)) {
          const ALLOWED = ['.pdf', '.doc', '.docx'];
          this.docsModalList = res.data.filter(
            (doc) => ALLOWED.includes((doc.mimeType || '').toLowerCase()),
          );
        } else {
          this.docsModalError = 'Could not load documents for this policy.';
        }
      } catch {
        this.docsModalError = 'An error occurred while fetching documents.';
      } finally {
        this.docsModalLoading = false;
      }
    },
    openDocument(doc) {
      const docId = typeof doc === 'object' ? doc.id : doc;
      const docName = typeof doc === 'object' ? (doc.actualName || `Document #${docId}`) : `Document #${docId}`;
      trackAgentInteraction(this, 'conversions_open_document', {
        document_id: docId,
        document_name: docName,
      });

      this.docViewerTitle = docName;
      this.docViewerLoading = false;
      const basePath = process.env.NODE_ENV === 'production' ? '/api' : '';
      this.docViewerUrl = `${basePath}/customer/document-api/v1/viewUWDocs/${docId}`;
      this.docViewerVisible = true;
    },
    closeDocViewer() {
      this.docViewerVisible = false;
      this.docViewerUrl = '';
    },
    openDocFull() {
      if (this.docViewerUrl) {
        trackAgentInteraction(this, 'conversions_open_document_full', {
          document_name: this.docViewerTitle || 'unknown',
        });
        window.open(this.docViewerUrl, '_blank', 'noopener,noreferrer');
      }
    },
    formatDate(val) {
      if (!val) return '—';
      try {
        return new Date(val).toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        });
      } catch {
        return val;
      }
    },
    formatCurrency(value) {
      if (value == null) return '';
      return `Ksh ${Number(value).toLocaleString()}`;
    },
  },
  metaInfo: { title: 'Conversions' },
};
</script>

<style scoped>
/* ── Page Header ── */
.page-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
}

.page-title {
  font-size: 1.35rem;
  font-weight: 800;
  color: #1E4276;
  margin: 0 0 4px 0;
}

.page-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}

/* ── Stats Chip ── */
.stats-chip {
  background: linear-gradient(135deg, #1E4276 0%, #2A5A9E 100%);
  border-radius: 14px;
  padding: 14px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 110px;
  box-shadow: 0 4px 12px rgba(30, 66, 118, 0.2);
}

.stats-chip-label {
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.stats-chip-value {
  font-size: 2rem;
  font-weight: 800;
  color: white;
  line-height: 1.1;
  margin-top: 2px;
}

/* ── Type Switch ── */
.type-switch {
  display: inline-flex;
  background: #F1F5F9;
  border-radius: 10px;
  padding: 4px;
  gap: 2px;
}

.type-switch-btn {
  padding: 8px 20px;
  border: none;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: #64748B;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.type-switch-btn:hover {
  color: #1E4276;
}

.type-switch-btn--active {
  background: white;
  color: #00AEEF;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* ── Search ── */
.search-wrapper {
  display: flex;
  align-items: center;
  background: white;
  border: 1.5px solid #E2E8F0;
  border-radius: 10px;
  padding: 0 14px;
  max-width: 380px;
  height: 48px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.search-wrapper:focus-within {
  border-color: #00AEEF;
  box-shadow: 0 0 0 3px rgba(0, 174, 239, 0.12);
}

.search-icon {
  margin-right: 8px;
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.9rem;
  color: #1E4276;
  background: transparent;
}

.search-input::placeholder {
  color: #94A3B8;
}

.search-clear {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
}

/* ── Claims Card ── */
.claims-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #E8EDF2;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

/* ── Table ── */
.table-scroll-wrap {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 1px;
}

.claims-table {
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
}

@media (max-width: 640px) {
  .claims-table th,
  .claims-table td {
    padding: 12px 14px;
    font-size: 0.85rem;
  }
}

.claims-table thead tr {
  background: #F8FAFC;
  border-bottom: 2px solid #E8EDF2;
}

.claims-table th {
  text-align: left;
  padding: 14px 20px;
  font-size: 0.8rem;
  font-weight: 700;
  color: #1E4276;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
}

.claims-row {
  border-bottom: 1px solid #F1F5F9;
  cursor: pointer;
  transition: background 0.15s;
}

.claims-row:last-child {
  border-bottom: none;
}

.claims-row:hover {
  background: #F0F9FF;
}

.claims-table td {
  padding: 16px 20px;
  vertical-align: middle;
}

.client-name {
  font-weight: 700;
  color: #1E4276;
  font-size: 0.95rem;
}

.cell-text {
  font-size: 0.9rem;
  color: #334155;
}

.cell-muted {
  color: #94A3B8;
  font-size: 0.875rem;
}

/* ── Cover Badges ── */
.cover-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 0.78rem;
  font-weight: 700;
  white-space: nowrap;
}

.badge-comprehensive {
  background: #EBF8FF;
  color: #00AEEF;
}

/* ── View Button ── */
.btn-view {
  background: transparent;
  border: 1.5px solid #00AEEF;
  color: #00AEEF;
  padding: 6px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.82rem;
  cursor: pointer;
  transition: all 0.18s;
  white-space: nowrap;
}

.btn-view:hover {
  background: #00AEEF;
  color: white;
}

/* ── Empty State ── */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #1E4276;
  margin: 16px 0 6px;
}

.empty-subtitle {
  font-size: 0.875rem;
  color: #94A3B8;
  margin: 0;
}
/* ── Shimmer Loading ── */

.shimmer {
  position: relative;
  overflow: hidden;
  background: #E9EEF3;
  border-radius: 6px;
}

.shimmer::after {
  content: '';
  position: absolute;
  top: 0;
  left: -150%;
  width: 150%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.65) 50%,
    transparent 100%
  );
  animation: conversion-shimmer 1.5s infinite;
}

@keyframes conversion-shimmer {
  0% {
    left: -150%;
  }

  100% {
    left: 150%;
  }
}

/* Total conversions number */
.shimmer-stat-value {
  width: 64px;
  height: 34px;
  margin-top: 4px;
  border-radius: 6px;
}

/* Table skeletons */
.shimmer-client {
  width: 150px;
  height: 18px;
}

.shimmer-product {
  width: 120px;
  height: 30px;
  border-radius: 20px;
}

.shimmer-amount {
  width: 90px;
  height: 17px;
}

.shimmer-date {
  width: 85px;
  height: 17px;
}

.shimmer-action {
  width: 100px;
  height: 32px;
  border-radius: 8px;
}

.skeleton-row {
  cursor: default !important;
}

.skeleton-row:hover {
  background: transparent !important;
}
</style>

<style>
.conversion-modal .v-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.conversion-modal-card {
  border-radius: 12px;
}

.conversion-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 0;
}

.conversion-modal-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #184679;
  margin: 0;
}

.conversion-modal-close {
  color: #718096;
}

.conversion-modal-body {
  padding: 24px !important;
}

.conversion-detail-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #E2E8F2;
  gap: 16px;
}

.conversion-detail-row:last-child {
  border-bottom: none;
}

.conversion-detail-label {
  font-size: 0.85rem;
  color: #718096;
  font-weight: 500;
  min-width: 120px;
}

.conversion-detail-value {
  font-size: 0.95rem;
  color: #2D3748;
  text-align: right;
}

.conversion-modal-actions {
  padding: 16px 24px !important;
}

.badge-info {
  background: #EBF8FF;
  color: #2B6CB0;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.85rem;
}

.docs-modal-card { border-radius: 14px !important; }
.docs-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 20px 20px 16px;
}
.docs-modal-title { font-size: 1.05rem; font-weight: 700; color: #184679; margin: 0; }
.docs-modal-subtitle { font-size: 0.78rem; color: #94A3B8; margin: 2px 0 0; }
.docs-modal-loader, .docs-modal-empty {
  display: flex; flex-direction: column;
  align-items: center; justify-content: center;
  padding: 40px 20px;
}
.docs-modal-list { max-height: 400px; overflow-y: auto; padding: 8px 0; }
.doc-list-item {
  display: flex; align-items: center; gap: 12px;
  padding: 12px 20px;
  border-bottom: 1px solid #F1F5F9;
  transition: background 0.15s;
}
.doc-list-item:hover { background: #F8FAFC; }
.doc-list-item:last-child { border-bottom: none; }
.doc-list-icon { width: 32px; display: flex; justify-content: center; flex-shrink: 0; }
.doc-list-info { flex: 1; min-width: 0; }
.doc-list-name {
  font-size: 0.875rem; font-weight: 600; color: #2D3748;
  margin: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.doc-list-meta { display: flex; align-items: center; gap: 8px; margin-top: 3px; }
.doc-type-badge {
  font-size: 0.7rem; font-weight: 700;
  background: #EBF8FF; color: #2B6CB0;
  padding: 1px 7px; border-radius: 10px;
}
.doc-date { font-size: 0.75rem; color: #94A3B8; }
.doc-view-btn { flex-shrink: 0; }

.doc-viewer-card {
  border-radius: 14px !important;
  display: flex;
  flex-direction: column;
  /* Use viewport height so it's readable */
  height: 90vh;
  max-height: 90vh;
  overflow: hidden;
}
.doc-viewer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #FAFBFC;
  flex-shrink: 0;
}
.doc-viewer-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #1E4276;
  margin: 0;
}
.doc-viewer-subtitle {
  font-size: 0.72rem;
  color: #94A3B8;
  margin: 0;
}
.doc-viewer-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 500px;
}
.doc-viewer-frame-wrap {
  flex: 1;
  overflow: hidden;
  display: flex;
}
.doc-viewer-iframe {
  width: 100%;
  /* Fill the full card height */
  height: 100%;
  min-height: 600px;
  border: none;
  display: block;
  flex: 1;
}
.doc-viewer-dialog {
  border-radius: 14px !important;
  overflow: hidden;
  /* Make it tall enough to read without opening full */
  margin: 24px auto !important;
}
</style>
